import { cn } from '@speedlo/ui'
import { observer } from 'mobx-react-lite'
import React from 'react'
import { Input, InputProps } from 'reactstrap'

import { useRoot } from '../../../app/useRoot'
import { useDebounce } from '../../../hooks/useDebounce'

interface IProps {
  onSearch: () => void
  isVisible: boolean
  inputProps: InputProps
}

export const AddressSearchInput = observer<IProps>(
  ({ onSearch, inputProps, isVisible }) => {
    const { location } = useRoot()
    const inputRef = React.useRef<Maybe<HTMLInputElement>>(null)

    React.useEffect(
      function autoFocus() {
        if (isVisible && inputRef.current !== null) {
          inputRef.current.focus()
        }
      },
      [isVisible],
    )

    const onSearchDebounced = useDebounce(onSearch, 2000)

    React.useEffect(() => {
      return () => onSearchDebounced.clear()
    }, [onSearchDebounced])

    const onChange = function searchOnChangeDebounced(
      ev: React.ChangeEvent<HTMLInputElement>,
    ) {
      const search = ev.target.value || ''
      location.setSearchValue(search)
      if (search.trim().length === 0) {
        location.clearAddress()
      }
      onSearchDebounced()
    }

    const onKeyPress = function searchOnEnterKey(
      ev: React.KeyboardEvent<HTMLInputElement>,
    ) {
      if (ev.key === 'Enter') {
        onSearchDebounced.clear()
        onSearch()
      }
    }

    const onFocus = function autoSelectOnFocus() {
      inputRef.current!.select()
    }

    return (
      <Input
        className={cn({ hidden: !isVisible })}
        onChange={onChange}
        onKeyPress={onKeyPress}
        onFocus={onFocus}
        value={location.searchValue}
        innerRef={inputRef}
        data-testid="SearchInput"
        {...inputProps}
      />
    )
  },
)
